import { add, norm, sub, type Vec3 } from "./coords";
import { heliocentricEcliptic, moonGeocentric, type BodyId } from "./positions";
import { SUPPORTED_END, SUPPORTED_START } from "./time";

/** 太阳引力参数 k²（高斯引力常数的平方），AU³/d²。 */
export const MU_SUN = 0.01720209895 ** 2;
/** 地月系引力参数（GM_earth + GM_moon），AU³/d²。 */
export const MU_EARTH_MOON = 8.997e-10;

export interface OrbitElements { a: number; e: number; periodDays: number }
export interface OrbitShape {
  /** 沿轨道均匀（偏近点角）取样的闭合折线，终点 = 起点。日心系（月球为地心系），黄道 J2000，AU。 */
  points: Vec3[];
  elements: OrbitElements;
  /** 轨道面法向（单位向量，黄道 J2000；由 r×v 得到，即轨道角动量方向）。 */
  normal: Vec3;
}

const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const scale = (a: Vec3, k: number): Vec3 => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const dotp = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;

/** 中心差分求速度（AU/d）；靠近支持范围两端时退化为单侧差分。 */
function velocity(pos: (d: Date) => Vec3, date: Date, h = 0.25): Vec3 {
  const t = date.getTime(), dt = h * 86400000;
  const lo = Math.max(SUPPORTED_START, t - dt), hi = Math.min(SUPPORTED_END, t + dt);
  const a = pos(new Date(lo)), b = pos(new Date(hi));
  return scale(sub(b, a), 1 / ((hi - lo) / 86400000));
}

/**
 * 由位置/速度求密切椭圆。轨道恰好经过天体当前位置，且在整个支持范围内都有定义
 * （不需要对前后一个周期取样，冥王星 248 年也没问题）。
 */
export function osculatingOrbit(id: BodyId, date: Date, samples = 192): OrbitShape {
  if (id === "sun") throw new Error("sun has no orbit");
  const isMoon = id === "moon";
  const mu = isMoon ? MU_EARTH_MOON : MU_SUN;
  const pos = (d: Date) => (isMoon ? moonGeocentric(d) : heliocentricEcliptic(id, d));
  const r = pos(date), v = velocity(pos, date, isMoon ? 0.05 : 0.25);
  const rn = norm(r), v2 = dotp(v, v);
  const h = cross(r, v), hn = norm(h);
  const evec = sub(scale(cross(v, h), 1 / mu), scale(r, 1 / rn));
  const e = norm(evec);
  const a = 1 / (2 / rn - v2 / mu);
  if (!(a > 0) || e >= 1) throw new Error(`${id}: orbit not a bound ellipse (a=${a}, e=${e})`);
  const P = e > 1e-6 ? scale(evec, 1 / e) : scale(r, 1 / rn); // 近圆轨道：以当前位置为起点
  const Q = scale(cross(h, P), 1 / hn);
  const b = a * Math.sqrt(1 - e * e);
  const points: Vec3[] = [];
  for (let i = 0; i <= samples; i++) {
    const E = (i / samples) * Math.PI * 2;
    const x = a * (Math.cos(E) - e), y = b * Math.sin(E);
    points.push(add(scale(P, x), scale(Q, y)));
  }
  points[samples] = { ...points[0]! }; // 精确闭合（避免 cos(2π) 的浮点残差）
  return { points, normal: scale(h, 1 / hn), elements: { a, e, periodDays: 2 * Math.PI * Math.sqrt((a * a * a) / mu) } };
}
